import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Shield, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useContent } from "@/context/ContentContext";

const BENEFIT_ICONS = [
  <Truck key="t" size={20} />,
  <Shield key="s" size={20} />,
  <Sparkles key="sp" size={20} />,
];

export default function Home() {
  const { content } = useContent();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 4)));
  }, []);

  const hero = content.hero || {};
  const cats = content.categories_section || {};
  const feat = content.featured_section || {};
  const edit = content.editorial_cta || {};
  const benefits = content.benefits || [];

  return (
    <main data-testid="page-home">
      {/* Hero */}
      <section className="relative h-[85vh] min-h-[600px] overflow-hidden">
        {hero.image_url && <img src={hero.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#F9F8F6]/95 via-[#F9F8F6]/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 h-full flex items-center">
          <div className="max-w-xl fade-in">
            <p className="label-sm mb-6">{hero.eyebrow}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl brand-text leading-[1.05] mb-8">
              {hero.title_line1}<br /><em>{hero.title_line2}</em>
            </h1>
            <p className="text-base brand-muted mb-10 max-w-md leading-relaxed">{hero.description}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/loja" className="btn-primary" data-testid="hero-cta-shop">
                {hero.cta_primary} <ArrowRight size={16} />
              </Link>
              <Link to="/sobre" className="btn-ghost" data-testid="hero-cta-about">{hero.cta_secondary}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      {benefits.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-3 gap-8 border-b brand-border">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="brand-primary p-3 rounded-sm">{BENEFIT_ICONS[i % 3]}</div>
              <div>
                <h4 className="font-serif text-xl brand-text mb-1">{b.title}</h4>
                <p className="text-sm brand-muted">{b.text}</p>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="mb-12">
          <p className="label-sm mb-3">{cats.eyebrow}</p>
          <h2 className="font-serif text-4xl lg:text-5xl brand-text">{cats.title}</h2>
        </div>
        <div className={`grid grid-cols-1 ${(cats.items?.length || 0) >= 3 ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2"} gap-8 stagger`}>
          {(cats.items || []).map((c, i) => (
            <Link
              key={c.slug || i}
              to={`/loja?category=${encodeURIComponent(c.slug || c.name)}`}
              className="group relative overflow-hidden aspect-[5/4]"
              data-testid={`cat-${(c.slug || c.name || "").toLowerCase()}`}
            >
              {c.image_url && (
                <img
                  src={c.image_url}
                  alt={c.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <p className="label-sm text-white/90 mb-2">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-serif text-4xl text-white mb-4">{c.name}</h3>
                <span className="inline-flex items-center gap-2 text-sm text-white uppercase tracking-wider">
                  Ver mais <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <p className="label-sm mb-3">{feat.eyebrow}</p>
            <h2 className="font-serif text-4xl lg:text-5xl brand-text">{feat.title}</h2>
          </div>
          <Link to="/loja" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-wider brand-text hover:brand-primary-text">
            Ver todos <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10 stagger" data-testid="featured-grid">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Editorial CTA */}
      {(edit.title || edit.description) && (
        <section className="brand-surface py-24 mt-10">
          <div className="max-w-3xl mx-auto px-6 text-center">
            {edit.eyebrow && <p className="label-sm mb-4">{edit.eyebrow}</p>}
            <h2 className="font-serif text-4xl lg:text-5xl brand-text mb-6">{edit.title}</h2>
            <p className="brand-muted mb-10">{edit.description}</p>
            {edit.cta_text && <Link to="/loja" className="btn-primary">{edit.cta_text} <ArrowRight size={16} /></Link>}
          </div>
        </section>
      )}
    </main>
  );
}
